import React, { useState, useEffect } from 'react';
import { Globe, X } from 'lucide-react';
import { Button } from './ui/button';

export const LanguagePreference = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [browserLang, setBrowserLang] = useState('en');

  useEffect(() => {
    // Check if user has already made a language choice
    const langPreference = localStorage.getItem('languagePreference');
    if (!langPreference) {
      // Detect browser language
      const detectedLang = navigator.language || navigator.userLanguage || 'en';
      const primaryLang = detectedLang.split('-')[0].toLowerCase();
      setBrowserLang(primaryLang);
      
      // Only show if browser is not English
      if (primaryLang !== 'en') {
        // Show after a short delay
        const timer = setTimeout(() => {
          setIsVisible(true);
        }, 500);
        return () => clearTimeout(timer);
      } else {
        // English browser - save preference and don't show
        localStorage.setItem('languagePreference', 'en');
      }
    }
  }, []);

  const handleEnglish = () => {
    localStorage.setItem('languagePreference', 'en');
    setIsVisible(false);
  };

  const handleTranslate = () => {
    localStorage.setItem('languagePreference', 'translate');
    setIsVisible(false);
    // Trigger browser translation hint by setting lang attribute
    document.documentElement.lang = browserLang;
    // Show a helpful message
    alert(`To translate this page:\n\n• Chrome/Edge: Right-click → "Translate to ${getLanguageName(browserLang)}"\n• Safari: Use the translate icon in the address bar\n• Firefox: Install a translation extension\n\nYour browser will remember this preference.`);
  };

  const handleClose = () => {
    localStorage.setItem('languagePreference', 'en');
    setIsVisible(false);
  };

  const getLanguageName = (code) => {
    const names = {
      zh: 'Chinese (中文)',
      de: 'German (Deutsch)',
      fr: 'French (Français)',
      es: 'Spanish (Español)',
      ja: 'Japanese (日本語)',
      ko: 'Korean (한국어)',
      ru: 'Russian (Русский)',
      pt: 'Portuguese (Português)',
      it: 'Italian (Italiano)',
      ar: 'Arabic (العربية)',
      nl: 'Dutch (Nederlands)',
    };
    return names[code] || code.toUpperCase();
  };

  if (!isVisible) return null;

  return (
    <div 
      data-testid="language-preference-modal"
      className="fixed bottom-20 right-4 z-[60] animate-scale-in"
    >
      <div className="bg-white rounded-xl shadow-2xl w-72 p-4 relative border border-gray-200">
        {/* Close button */}
        <button
          onClick={handleClose}
          className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 transition-colors"
          aria-label="Close"
        >
          <X className="w-4 h-4" />
        </button>

        {/* Header */}
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
            <Globe className="w-5 h-5 text-blue-600" />
          </div>
          <div>
            <h2 className="text-sm font-bold text-gray-900">
              Language Preference
            </h2>
            <p className="text-xs text-gray-500">
              Browser: {getLanguageName(browserLang)}
            </p>
          </div>
        </div>

        {/* Buttons */}
        <div className="space-y-2">
          <Button
            onClick={handleEnglish}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white py-2 text-sm font-medium"
            data-testid="lang-english-btn"
          >
            🇬🇧 Continue in English
          </Button>
          <Button
            onClick={handleTranslate}
            variant="outline"
            className="w-full border border-gray-300 text-gray-700 hover:bg-gray-50 py-2 text-sm font-medium"
            data-testid="lang-translate-btn"
          >
            🌐 Translate Page
          </Button>
        </div>
      </div>
    </div>
  );
};
